import { useId } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { validateEnvVarKey } from '@/lib/deployPlan';

export interface EnvVarEditorProps {
  /** Effective values (defaults merged with edits). */
  envVars: Readonly<Record<string, string>>;
  /** The selected stack's defaults; their keys cannot be removed. */
  defaults: Readonly<Record<string, string>>;
  onSet: (key: string, value: string) => void;
  onAdd: (key: string, value: string) => void;
  onRemove: (key: string) => void;
  /** The add-variable row inputs; owned by the page so they survive step navigation. */
  draft: EnvDraft;
  onDraftChange: (draft: EnvDraft) => void;
}

export interface EnvDraft {
  key: string;
  value: string;
}

const PLACEHOLDERS: { code: string; text: string }[] = [
  { code: '[generate_secret]', text: '32-char alphanumeric secret.' },
  { code: '[generate_secret:length]', text: 'Custom length alphanumeric secret.' },
  { code: '[generate_password]', text: '32-char strong password (containing uppercase, lowercase, numbers, and symbols).' },
  { code: '[generate_password:length]', text: 'Custom length strong password (minimum length 4).' },
  { code: '[generate_secret:base64:bytes]', text: 'Random bytes base64 encoded.' },
  { code: '[generate_secret:hex:bytes]', text: 'Random bytes hex encoded.' },
];

const FOCUS = 'outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Use inside the Configure step: edit the stack's env vars, add custom ones, and read the placeholder help. */
export function EnvVarEditor({ envVars, defaults, onSet, onAdd, onRemove, draft, onDraftChange }: EnvVarEditorProps) {
  const uid = useId();
  const { key, value } = draft;

  const entries = Object.entries(envVars);
  const invalid = key !== '' && !validateEnvVarKey(key);
  const duplicate = key !== '' && Object.hasOwn(envVars, key);
  const canAdd = key !== '' && !invalid && !duplicate;
  const msgId = `${uid}-msg`;

  const add = () => {
    if (!canAdd) return;
    onAdd(key, value);
    onDraftChange({ key: '', value: '' });
  };

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium">Environment variables</h3>
      <p className="text-xs text-muted-foreground">Default values come from the stack. Add custom variables below.</p>

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No default env vars for this stack.</p>
      ) : (
        <ul className="space-y-2">
          {entries.map(([k, v]) => {
            const isDefault = Object.hasOwn(defaults, k);
            return (
              <li key={k} className="flex items-center gap-2">
                <Input value={k} readOnly aria-label={`Key ${k}`} title={k} className="flex-1 bg-muted font-mono" />
                <Input
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  value={v}
                  onChange={(e) => onSet(k, e.target.value)}
                  placeholder={isDefault ? defaults[k] : ''}
                  aria-label={`Value for ${k}`}
                  className="flex-1 font-mono"
                />
                {isDefault ? (
                  <span className="size-8 shrink-0" aria-hidden />
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemove(k)}
                    aria-label={`Remove ${k}`}
                    className={`shrink-0 text-destructive hover:text-destructive ${FOCUS}`}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="space-y-2 border-t pt-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1 space-y-1">
            <Label htmlFor={`${uid}-key`} className="text-xs text-muted-foreground">
              New variable key
            </Label>
            <Input
              id={`${uid}-key`}
              value={key}
              onChange={(e) => onDraftChange({ key: e.target.value, value })}
              placeholder="KEY"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={invalid || duplicate || undefined}
              aria-describedby={invalid || duplicate ? msgId : undefined}
              className="font-mono"
            />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <Label htmlFor={`${uid}-value`} className="text-xs text-muted-foreground">
              New variable value
            </Label>
            <Input
              id={`${uid}-value`}
              type="text"
              value={value}
              onChange={(e) => onDraftChange({ key, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder="VALUE"
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
            />
          </div>
          <Button type="button" variant="outline" size="sm" onClick={add} disabled={!canAdd} className={`self-start sm:self-auto ${FOCUS}`}>
            Add variable
          </Button>
        </div>
        {invalid && (
          <p id={msgId} className="text-xs text-destructive">
            Must start with a letter or underscore and contain only letters, numbers and underscores (max 64)
          </p>
        )}
        {!invalid && duplicate && (
          <p id={msgId} className="text-xs text-destructive">
            Key already exists
          </p>
        )}
      </div>

      <details className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
        <summary className={`cursor-pointer rounded-sm font-medium text-foreground ${FOCUS}`}>Secret generation placeholders</summary>
        <div className="mt-2 space-y-2">
          <p>Values matching the following placeholders will generate secure random secrets upon deployment:</p>
          <ul className="list-disc space-y-0.5 pl-4">
            {PLACEHOLDERS.map((p) => (
              <li key={p.code}>
                <code className="font-mono text-foreground">{p.code}</code>: {p.text}
              </li>
            ))}
          </ul>
          <p>Secrets are generated once during deploy and saved in the app details.</p>
        </div>
      </details>
    </section>
  );
}
